// Update Behavior: for each employee in the teams in view, the manager records
// the capability level they have most recently observed in them on the job,
// capability by capability. This is what Insights' Behavior tab reports. It
// never changes the employee's Capability level, which only Elliott's grading
// of Roleplays and Quiz Questions sets.

import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SourceChip from '../SourceChip';
import { sourceOf } from '../capabilitySource';
import { LEVEL_COLOR, LEVEL_NAME, LevelLabel } from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import {
  LEVEL_ORDER,
  type CapabilityLevel,
  type CapabilityPair
} from '../../Program/components/learning-lifecycle/teamCapability';
import type { Person } from '../../Program/components/program tabs/insights/insightsData';
import type { LevelCriteria } from '../capabilitySetup';
import type { OperationsRoleView, OperationsView } from '../../../../../api/capability';
import {
  BODY,
  BRANDY,
  BRANDY_FILL,
  BRANDY_FRAME,
  BRANDY_TINT,
  FONT,
  LABEL,
  LINE,
  SCROLL,
  TEXT,
  TEXT_2,
  TEXT_3
} from '../designSystem';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** One answer: not observed first hand, or a level with what it looks like. */
const Option: React.FC<{ selected: boolean; title: string; color?: string; text?: string; onClick: () => void }> = ({
  selected,
  title,
  color,
  text,
  onClick
}) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      p: '9px 12px',
      textAlign: 'left',
      border: `1px solid ${selected ? BRANDY : LINE}`,
      borderRadius: '4px',
      bgcolor: selected ? BRANDY_FILL : 'transparent',
      cursor: 'pointer',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': { borderColor: selected ? BRANDY : TEXT_3 }
    }}
  >
    {/* The radio mark. */}
    <Box
      sx={{
        flexShrink: 0,
        width: 14,
        height: 14,
        mt: '2px',
        borderRadius: '50%',
        border: `1.5px solid ${selected ? BRANDY : TEXT_3}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {selected && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: BRANDY }} />}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: color ? 11 : 13,
          fontWeight: color ? 700 : 500,
          letterSpacing: color ? '0.06em' : 0,
          textTransform: color ? 'uppercase' : 'none',
          color: color ?? TEXT
        }}
      >
        {title}
      </Typography>
      {text && <Typography sx={{ ...BODY, fontSize: 12.5, mt: '2px' }}>{text}</Typography>}
    </Box>
  </Box>
);

const UpdateBehavior: React.FC<{
  people: Person[];
  /** Everyone's capability levels (the capabilities of their role, and where they stand on each). */
  pairs: CapabilityPair[];
  /** The roles set up in Capability Setup, for each capability's own level descriptions. */
  roles: OperationsRoleView[];
  /** The level that stands for each person on each capability: by person id, then capability id. */
  observed: OperationsView['observed'];
  /** The level observed, or null to clear it (nothing observed first hand). Saved at once. */
  onObserve: (personId: string, capabilityId: string, level: CapabilityLevel | null) => unknown;
}> = ({ people, pairs, roles, observed, onObserve }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const person = people.find((p) => p.id === selectedId) ?? people[0] ?? null;
  // A phone shows one pane at a time: the learners, then the one tapped, with a way back.
  const isMobileApp = useIsMobileApp();
  const opened = isMobileApp ? people.find((p) => p.id === selectedId) ?? null : person;
  // Where the phone's list was when a learner was opened, so going back returns to it.
  const listScrollTop = useRef(0);

  // A person's capabilities and how many of them the manager has answered.
  const capabilitiesOf = useMemo(() => {
    const byPerson = new Map<string, CapabilityPair[]>();
    pairs.forEach((pair) => byPerson.set(pair.personId, [...(byPerson.get(pair.personId) ?? []), pair]));
    return byPerson;
  }, [pairs]);
  const answered = (id: string) => Object.keys(observed[id] ?? {}).length;

  if (!person) {
    return <Typography sx={{ ...BODY, color: TEXT_3, p: '16px' }}>No learners in the selected teams yet.</Typography>;
  }
  // The levels as the role's manager wrote them in Capability Setup.
  const criteriaFor = (pair: CapabilityPair): LevelCriteria =>
    roles.find((r) => r.id === pair.roleId)?.capabilities.find((c) => c.id === pair.capabilityId)?.criteria ?? {};

  // The employees, with how far through each one the manager is.
  const learnerList = (
    <Box
      sx={{
        flex: '0 0 280px',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${LINE}`,
        borderRadius: '4px',
        [MOBILE_SX]: { flex: 'none' }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          p: '9px 12px',
          bgcolor: '#232323',
          borderBottom: `1px solid ${LINE}`,
          borderRadius: '4px 4px 0 0'
        }}
      >
        <Typography sx={{ ...LABEL, flex: 1 }}>Learner</Typography>
        <Typography sx={LABEL}>Observed</Typography>
      </Box>
      <Box sx={{ ...SCROLL, flex: 1, minHeight: 0, [MOBILE_SX]: { overflowY: 'visible' } }}>
        {people.map((p) => {
          const on = p.id === opened?.id;
          const total = capabilitiesOf.get(p.id)?.length ?? 0;
          const done = answered(p.id);
          return (
            <Box
              key={p.id}
              component="button"
              type="button"
              aria-pressed={on}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                setSelectedId(p.id);
                // A phone opens the learner as a new screen, from its top, and remembers the list's place.
                if (isMobileApp) {
                  const scroller = e.currentTarget.closest('.dosen-app-scroll');
                  listScrollTop.current = scroller?.scrollTop ?? 0;
                  scroller?.scrollTo({ top: 0 });
                }
              }}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                p: '8px 12px',
                textAlign: 'left',
                border: 'none',
                borderBottom: `1px solid ${LINE}`,
                borderLeft: `2px solid ${on ? BRANDY : 'transparent'}`,
                bgcolor: on ? BRANDY_TINT : 'transparent',
                cursor: 'pointer',
                '&:hover': { bgcolor: on ? BRANDY_TINT : '#151515' },
                [MOBILE_SX]: { minHeight: 52, p: '10px 12px' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 13,
                    fontWeight: 600,
                    color: TEXT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    [MOBILE_SX]: { fontSize: 14, whiteSpace: 'normal' }
                  }}
                >
                  {p.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 11,
                    color: TEXT_3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    [MOBILE_SX]: { fontSize: 12, whiteSpace: 'normal' }
                  }}
                >
                  {p.roleName} · {p.teamName}
                </Typography>
              </Box>
              <Typography
                sx={{
                  flexShrink: 0,
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 600,
                  color: total > 0 && done === total ? '#00972D' : TEXT_2
                }}
              >
                {done}/{total}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (!opened) return learnerList;
  const mine = capabilitiesOf.get(opened.id) ?? [];

  // The employee in view: each capability, their level on it, and what the manager has observed.
  const behavior = (
    <Box
      sx={{
        ...SCROLL,
        flex: 1,
        // A brandy rule down the left, between the employees and this employee's behavior.
        borderLeft: BRANDY_FRAME,
        pl: '16px',
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        pr: '4px',
        [MOBILE_SX]: { flex: 'none', borderLeft: 'none', pl: 0, pr: 0, overflowY: 'visible' }
      }}
    >
      {/* What to do here, and when to leave a capability alone. */}
      <Typography sx={{ ...BODY, flexShrink: 0, color: TEXT }}>
        Choose the level you have most recently seen from {firstName(opened.name)} on the job. Leave a capability blank
        if you have not seen it first hand.
      </Typography>
      {mine.length === 0 && (
        <Typography sx={{ ...BODY, color: TEXT_3 }}>{opened.roleName} has no capabilities set up yet.</Typography>
      )}
      {mine.map((pair, index) => {
        const criteria = criteriaFor(pair);
        const current = observed[opened.id]?.[pair.capabilityId]?.level;
        return (
          <Box
            key={pair.capabilityId}
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              // A brandy rule between one capability and the next.
              ...(index > 0 ? { borderTop: BRANDY_FRAME, pt: '14px', mt: '2px' } : null)
            }}
          >
            {/* The capability's title row, with the level Elliott has them at. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: 36,
                pl: '12px',
                pr: '12px',
                border: BRANDY_FRAME,
                borderRadius: '4px',
                bgcolor: BRANDY_TINT,
                // A phone: the name on its own row, whole; the chip and the level beneath it.
                [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '6px', py: '8px' }
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: FONT,
                  fontSize: 14,
                  textTransform: 'uppercase',
                  color: TEXT,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  [MOBILE_SX]: { flex: '1 1 100%', whiteSpace: 'normal' }
                }}
              >
                {pair.capabilityName}
              </Typography>
              <SourceChip
                source={sourceOf(
                  pair.source
                    ? pair
                    : roles.find((r) => r.id === pair.roleId)?.capabilities.find((c) => c.id === pair.capabilityId)
                )}
              />
              <Typography sx={{ ...LABEL, flexShrink: 0 }}>Capability Level</Typography>
              {pair.level ? (
                <LevelLabel level={pair.level} />
              ) : (
                <Typography sx={{ flexShrink: 0, fontFamily: FONT, fontSize: 11, color: TEXT_3 }}>
                  Not assessed yet
                </Typography>
              )}
            </Box>
            <Box
              role="radiogroup"
              aria-label={`Observed level on ${pair.capabilityName}`}
              sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              {LEVEL_ORDER.map((level) => (
                <Option
                  key={level}
                  selected={current === level}
                  title={LEVEL_NAME[level]}
                  color={LEVEL_COLOR[level]}
                  text={criteria[level]}
                  onClick={() => onObserve(opened.id, pair.capabilityId, current === level ? null : level)}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  if (isMobileApp) {
    return (
      // Keyed, so the list (whose rows can share a key: one person in two teams) is unmounted whole.
      <Box key="learner" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box
          component="button"
          type="button"
          onClick={() => {
            setSelectedId(null);
            requestAnimationFrame(() =>
              document.querySelector('.dosen-app-scroll')?.scrollTo({ top: listScrollTop.current })
            );
          }}
          sx={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            minHeight: 40,
            ml: '-6px',
            p: 0,
            pr: '8px',
            border: 'none',
            bgcolor: 'transparent',
            color: BRANDY,
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
          All learners
        </Box>
        <Box sx={{ pb: '4px', borderBottom: `1px solid ${LINE}` }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: TEXT }}>{opened.name}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_3, mt: '2px' }}>
            {opened.roleName} · {opened.teamName}
          </Typography>
        </Box>
        {behavior}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: '16px' }}>
      {learnerList}
      {behavior}
    </Box>
  );
};

export default UpdateBehavior;
